// ============================================================================
// CUSTOMER RESEARCH TAB
// Full research tab for project detail page: launch research, view tasks,
// and explore the knowledge graph built from research outputs
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Globe,
  Users,
  Building2,
  Zap,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  RefreshCw
} from 'lucide-react';
import {
  customerResearchService,
  projectTasksService,
  ProjectTaskSummary,
  LaunchResearchInput
} from '../../services/sessionTasksService';
import { CustomerResearchGraph } from './CustomerResearchGraph';

interface CustomerResearchTabProps {
  projectId: string;
  token: string;
}

const ORGANISATION_TYPE_OPTIONS = [
  'Enterprise',
  'SME',
  'Startup',
  'NPO / Charity',
  'Public Sector (Council)',
  'Public Sector (NHS)',
  'Public Sector (Government)',
  'Other'
];

const FOCUS_AREA_OPTIONS = [
  { id: 'systems', label: 'Systems', icon: Zap },
  { id: 'contracts', label: 'Contracts', icon: Globe },
  { id: 'people', label: 'People', icon: Users },
  { id: 'strategy', label: 'Strategy', icon: Building2 },
];

const RESEARCH_TEMPLATE_IDS = [
  'session-task-customer-research',
  'session-task-graph-sync'
];

export const CustomerResearchTab: React.FC<CustomerResearchTabProps> = ({
  projectId,
  token
}) => {
  // Launch research form state
  const [showLaunchForm, setShowLaunchForm] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerWebsite, setCustomerWebsite] = useState('');
  const [organisationType, setOrganisationType] = useState('');
  const [focusAreas, setFocusAreas] = useState<Set<string>>(new Set());
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Tasks state
  const [tasks, setTasks] = useState<ProjectTaskSummary[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState<string | null>(null);

  // Load research tasks
  const loadTasks = useCallback(async () => {
    setTasksLoading(prev => tasks.length > 0 ? false : prev);
    setTasksError(null);
    try {
      const allTasks = await projectTasksService.listTasks(projectId);
      const researchTasks = allTasks.filter(
        t => RESEARCH_TEMPLATE_IDS.includes(t.templateId)
      );
      setTasks(researchTasks);
    } catch (err: any) {
      setTasksError('Failed to load research tasks: ' + (err.message || 'Unknown error'));
    } finally {
      setTasksLoading(false);
    }
  }, [projectId, tasks.length]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Poll for updates when there are running tasks
  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(loadTasks, 15000);
    return () => clearInterval(interval);
  }, [tasks, loadTasks]);

  // Toggle focus area
  const toggleFocusArea = (area: string) => {
    setFocusAreas(prev => {
      const next = new Set(prev);
      if (next.has(area)) {
        next.delete(area);
      } else {
        next.add(area);
      }
      return next;
    });
  };

  // Launch research handler
  const handleLaunchResearch = async () => {
    if (!customerName.trim()) return;

    setLaunching(true);
    setLaunchError(null);

    try {
      const input: LaunchResearchInput = {
        customerName: customerName.trim(),
      };
      if (customerWebsite.trim()) {
        input.customerWebsite = customerWebsite.trim();
      }
      if (organisationType) {
        input.organisationType = organisationType;
      }
      if (focusAreas.size > 0) {
        input.focusAreas = Array.from(focusAreas);
      }

      await customerResearchService.launchResearch(projectId, input);

      // Success: reset form, show notification, refresh tasks
      setShowLaunchForm(false);
      setCustomerName('');
      setCustomerWebsite('');
      setOrganisationType('');
      setFocusAreas(new Set());
      setSuccessMessage('Research task launched successfully');
      setTimeout(() => setSuccessMessage(null), 4000);
      loadTasks();
    } catch (err: any) {
      setLaunchError('Failed to launch research: ' + (err.message || 'Unknown error'));
    } finally {
      setLaunching(false);
    }
  };

  // Format helpers (matching ProjectTasksTab patterns)
  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const durationMs = end - start;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'running':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            Running
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600">
            <CheckCircle size={12} />
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600">
            <XCircle size={12} />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
            <Clock size={12} />
            {status}
          </span>
        );
    }
  };

  const handleViewResults = (taskId: string) => {
    // Navigate to the Tasks tab with the task selected via URL search params
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'tasks');
    url.searchParams.set('taskId', taskId);
    window.open(url.toString(), '_self');
  };

  const completedTasks = tasks.filter(t => t.status === 'completed');
  const hasCompletedResearch = completedTasks.length > 0;

  return (
    <div className="flex flex-col gap-8">
      {/* Success notification */}
      {successMessage && (
        <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-3 rounded-xl border border-green-200 text-sm font-medium">
          <CheckCircle size={16} />
          {successMessage}
        </div>
      )}

      {/* ================================================================ */}
      {/* Section 1: Launch Research */}
      {/* ================================================================ */}
      <section className="bg-white rounded-xl border border-[#e2e8f0]">
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-lg font-bold text-navy">Customer Research</h2>
            <p className="text-sm text-slate-500 mt-0.5">Launch deep research tasks to build a customer knowledge graph</p>
          </div>
          {!showLaunchForm && (
            <button
              onClick={() => setShowLaunchForm(true)}
              className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-primary/20 hover:bg-primary-dark transition-colors"
            >
              <Plus size={18} />
              Launch Research
            </button>
          )}
        </div>

        {/* Inline launch form */}
        {showLaunchForm && (
          <div className="p-5 space-y-5">
            {launchError && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 text-sm">
                {launchError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Customer Name */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="e.g. Acme Corp, NHS Trust, City Council"
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                />
              </div>

              {/* Customer Website */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">
                  Customer Website
                </label>
                <input
                  type="text"
                  value={customerWebsite}
                  onChange={e => setCustomerWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                />
              </div>

              {/* Organisation Type */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">
                  Organisation Type
                </label>
                <select
                  value={organisationType}
                  onChange={e => setOrganisationType(e.target.value)}
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                >
                  <option value="">Select organisation type...</option>
                  {ORGANISATION_TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Focus Areas */}
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">
                Focus Areas
              </label>
              <div className="flex flex-wrap gap-3">
                {FOCUS_AREA_OPTIONS.map(area => {
                  const Icon = area.icon;
                  const isActive = focusAreas.has(area.id);
                  return (
                    <label
                      key={area.id}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all border ${
                        isActive
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-[#e2e8f0] bg-white text-slate-600 hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => toggleFocusArea(area.id)}
                        className="sr-only"
                      />
                      <Icon size={16} />
                      {area.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Form actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleLaunchResearch}
                disabled={!customerName.trim() || launching}
                className="flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-sm shadow-primary/20 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {launching ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Launching...
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    Start Research
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowLaunchForm(false);
                  setLaunchError(null);
                }}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ================================================================ */}
      {/* Section 2: Research Tasks */}
      {/* ================================================================ */}
      <section className="bg-white rounded-xl border border-[#e2e8f0]">
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-navy">Research Tasks</h2>
            <span className="px-2.5 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded-full">
              {tasks.length}
            </span>
          </div>
          <button
            onClick={loadTasks}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary font-medium transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>

        <div className="p-5">
          {/* Loading */}
          {tasksLoading && tasks.length === 0 && (
            <div className="text-center py-10">
              <Loader2 size={24} className="text-primary animate-spin mx-auto mb-3" />
              <p className="text-slate-500 text-sm font-medium">Loading research tasks...</p>
            </div>
          )}

          {/* Error */}
          {tasksError && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 text-sm mb-4">
              {tasksError}
            </div>
          )}

          {/* Empty */}
          {!tasksLoading && tasks.length === 0 && !tasksError && (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Search size={24} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No research tasks yet. Launch your first research to get started.</p>
            </div>
          )}

          {/* Task cards */}
          {tasks.length > 0 && (
            <div className="space-y-3">
              {tasks.map(task => (
                <div
                  key={task.taskId}
                  className="relative flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 bg-slate-50/50 rounded-xl border border-[#e2e8f0] hover:border-primary/30 transition-all"
                >
                  {/* Accent bar for running tasks */}
                  {task.status === 'running' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />
                  )}

                  <div className="flex items-start gap-3 flex-1">
                    {/* Status icon */}
                    <div className={`size-9 rounded-lg flex items-center justify-center shrink-0 ${
                      task.status === 'running' ? 'bg-primary/10 text-primary' :
                      task.status === 'completed' ? 'bg-green-50 text-green-600' :
                      task.status === 'failed' ? 'bg-red-50 text-red-600' :
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {task.status === 'running' ? <Loader2 size={18} className="animate-spin" /> :
                       task.status === 'completed' ? <CheckCircle size={18} /> :
                       task.status === 'failed' ? <XCircle size={18} /> :
                       <Clock size={18} />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="text-sm font-bold text-navy truncate">{task.taskName}</h4>
                        {statusBadge(task.status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {task.startedAt && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {formatDuration(task.startedAt, task.completedAt)}
                          </span>
                        )}
                        <span>
                          Created {formatTimeAgo(task.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {task.status === 'completed' && (
                      <button
                        onClick={() => handleViewResults(task.taskId)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-[#e2e8f0] text-xs font-bold text-navy hover:bg-slate-50 transition-colors flex items-center gap-1"
                      >
                        View Results
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ================================================================ */}
      {/* Section 3: Knowledge Graph */}
      {/* ================================================================ */}
      {hasCompletedResearch && (
        <section className="bg-white rounded-xl border border-[#e2e8f0]">
          <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-navy">Knowledge Graph</h2>
              <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded-full">
                {completedTasks.length} research {completedTasks.length === 1 ? 'source' : 'sources'}
              </span>
            </div>
          </div>
          <div className="p-5">
            <CustomerResearchGraph projectId={projectId} />
          </div>
        </section>
      )}
    </div>
  );
};

export default CustomerResearchTab;
